import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Store de estado de bloqueo de aplicación
 * 
 * Bounded Context: Autenticación
 * Responsabilidad: Gestionar estado de bloqueo temporal de la app
 * 
 * SEGURIDAD: Persist middleware asegura que estado persista en localStorage
 * para prevenir bypass por refresh de página
 */
interface LockState {
    /** Si la aplicación está bloqueada */
    isLocked: boolean;

    /** Timestamp del momento de bloqueo */
    lockedAt: Date | null;

    /** Bloquear aplicación */
    lock: () => void;

    /** Desbloquear aplicación */
    unlock: () => void;
}

/**
 * Hook useLock - Gestión de bloqueo temporal con persistencia
 * 
 * NOTA SEGURIDAD: Estado se guarda en localStorage para prevenir
 * bypass refrescando página o navegando a otras rutas
 * 
 * @example
 * const { isLocked, lock, unlock } = useLock();
 * 
 * // Bloquear app
 * lock();
 * 
 * // Desbloquear después de validar contraseña
 * unlock();
 */
export const useLock = create<LockState>()(
    persist(
        (set) => ({
            isLocked: false,
            lockedAt: null,

            lock: () => set({
                isLocked: true,
                lockedAt: new Date()
            }),

            unlock: () => set({
                isLocked: false,
                lockedAt: null
            }),
        }),
        {
            name: 'zafiro-lock-storage', // Key en localStorage
        }
    )
);
